import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, DataTable, DetailList, Dialog, Field } from "../../../ui";
import type { DataColumn, FieldElement } from "../../../ui";
import type { Schemas } from "../../workspace/api.generated";
import { failure } from "../../workspace/behavior";
import { date } from "../../workspace/model";
import { PageHeader, Panel, RowAction, statusBadge } from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import type { IpAllowlistMutation } from "../queries";
import { createIpAllowlistSchema } from "../schema";
import type { CreateIpAllowlistValues } from "../schema";

type QueryState = "ready" | "loading" | "error";
type Editor =
  | { readonly kind: "create"; readonly values: CreateIpAllowlistValues }
  | { readonly kind: "deactivate"; readonly item: Schemas["AdminIpAllowlistResponse"] };

const emptyValues = (): CreateIpAllowlistValues => ({ cidr: "", description: "" });

function allowlistFailure(cause: unknown) {
  const code = (cause as { code?: string } | null)?.code;
  const mapped: Readonly<Record<string, { readonly message: string; readonly reconcile: boolean; readonly field?: "cidr" }>> = {
    ADMIN_IP_CIDR_INVALID: { message: "IPv4 또는 IPv6 숫자 주소와 올바른 prefix를 입력해 주세요.", reconcile: false, field: "cidr" },
    ADMIN_IP_CIDR_DUPLICATED: { message: "이미 활성 상태로 등록된 CIDR입니다.", reconcile: false, field: "cidr" },
    LAST_ADMIN_IP_ALLOWLIST_PROTECTED: { message: "마지막 활성 IP 허용 범위는 비활성화할 수 없습니다.", reconcile: true },
    ADMIN_IP_ALLOWLIST_NOT_FOUND: { message: "선택한 IP 허용 범위를 찾을 수 없습니다.", reconcile: true },
  };
  return code && mapped[code] ? { ...failure(cause), ...mapped[code] } : failure(cause);
}

export interface IpAllowlistViewProps {
  readonly items?: readonly Schemas["AdminIpAllowlistResponse"][];
  readonly listState: QueryState;
  readonly selectedId?: number;
  readonly selected?: Schemas["AdminIpAllowlistResponse"];
  readonly canManage: boolean;
  readonly notice?: { readonly tone: "information" | "success"; readonly text: string };
  readonly onSelect: (id: number) => void;
  readonly onClearSelection: () => void;
  readonly onRetryList: () => void | Promise<unknown>;
  readonly onMutation: (input: IpAllowlistMutation) => Promise<unknown>;
  readonly onMutationComplete: (input: IpAllowlistMutation, result: unknown) => Promise<void>;
}

export function IpAllowlistView(props: IpAllowlistViewProps) {
  const [editor, setEditor] = useState<Editor>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState("");
  const [requestBlocked, setRequestBlocked] = useState(false);
  const [pending, setPending] = useState(false);
  const refs = useRef<Record<string, FieldElement | null>>({});
  const formId = useId();
  const activeCount = props.items?.filter((row) => row.active).length ?? 0;
  const lastActiveSelected = Boolean(props.selected?.active && activeCount <= 1);

  useEffect(() => {
    setEditor(undefined);
    setErrors({});
    setRequestError("");
    setRequestBlocked(false);
  }, [props.selectedId]);

  const setRef = (key: string) => (element: FieldElement | null) => { refs.current[key] = element; };
  const resetFeedback = () => { setErrors({}); setRequestError(""); setRequestBlocked(false); };
  const openEditor = (next: Editor) => { resetFeedback(); setEditor(next); };
  const closeEditor = () => {
    if (pending) return;
    setEditor(undefined);
    resetFeedback();
  };
  const updateCreate = (key: keyof CreateIpAllowlistValues, value: string) => {
    setEditor((current) => current?.kind === "create" ? { ...current, values: { ...current.values, [key]: value } } : current);
    setErrors((current) => ({ ...current, [key]: "" }));
    setRequestError("");
  };

  const submit = async () => {
    if (!editor || pending || requestBlocked) return;
    let input: IpAllowlistMutation;
    if (editor.kind === "create") {
      const parsed = createIpAllowlistSchema.safeParse(editor.values);
      if (!parsed.success) {
        const next: Record<string, string> = {};
        for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message;
        setErrors(next);
        refs.current[String(parsed.error.issues[0]?.path[0])]?.focus();
        return;
      }
      input = { kind: "create", body: { cidr: parsed.data.cidr, description: parsed.data.description || null } };
    } else {
      input = { kind: "deactivate", id: editor.item.id };
    }
    setPending(true);
    setRequestError("");
    try {
      const result = await props.onMutation(input);
      await props.onMutationComplete(input, result);
      setEditor(undefined);
    } catch (cause) {
      const feedback = allowlistFailure(cause);
      setRequestError(feedback.message);
      setRequestBlocked(feedback.reconcile);
      const field = "field" in feedback && feedback.field === "cidr" ? "cidr" : undefined;
      if (field) queueMicrotask(() => refs.current[field]?.focus());
    } finally {
      setPending(false);
    }
  };

  const reconcile = async () => {
    try {
      await props.onRetryList();
      setEditor(undefined);
      resetFeedback();
    } catch (cause) {
      setRequestError(failure(cause).message);
    }
  };
  const retryList = () => { void Promise.resolve(props.onRetryList()).catch(() => undefined); };

  const columns = [
    { key: "cidr", title: "CIDR", render: (row: Schemas["AdminIpAllowlistResponse"]) => <code>{row.cidr}</code> },
    { key: "description", title: "설명", render: (row: Schemas["AdminIpAllowlistResponse"]) => row.description || "미등록" },
    { key: "creator", title: "등록 관리자", render: (row: Schemas["AdminIpAllowlistResponse"]) => row.createdByAdminLoginId ?? "—" },
    { key: "created", title: "등록일시", render: (row: Schemas["AdminIpAllowlistResponse"]) => date(row.createdAt) },
    { key: "status", title: "상태", render: (row: Schemas["AdminIpAllowlistResponse"]) => statusBadge(row.active ? "ACTIVE" : "DISABLED") },
    { key: "action", title: "작업", render: (row: Schemas["AdminIpAllowlistResponse"]) => <RowAction onClick={() => props.onSelect(row.id)}>IP 범위 {row.id} 선택</RowAction> },
  ] satisfies readonly DataColumn<Schemas["AdminIpAllowlistResponse"]>[];

  return <>
    <PageHeader
      id="ADM-S22"
      title="IP 허용목록"
      description="관리자 시스템에 접근할 수 있는 IPv4·IPv6 네트워크 범위를 관리합니다."
      meta={<p aria-live="polite">전체 {props.items?.length ?? 0}건 · 활성 {activeCount}건</p>}
      action={<GuidedActionButton size="small" blockedReason={props.canManage ? null : "IP 허용목록 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요."} onClick={() => openEditor({ kind: "create", values: emptyValues() })}>네트워크 추가</GuidedActionButton>}
    />
    {!props.canManage ? <Alert persistent variant="warning" title="시스템 관리자 권한이 필요합니다">ROLE_SUPER와 ADMIN_MANAGE 권한이 모두 있어야 IP 허용목록을 관리할 수 있습니다.</Alert> : null}
    {props.notice ? <Alert persistent variant={props.notice.tone} title="IP 허용목록 처리 결과">{props.notice.text}</Alert> : null}
    <Alert persistent variant="warning" title="현재 접속 네트워크를 먼저 확인해 주세요">활성 범위를 비활성화하면 현재 위치에서 다음 로그인부터 관리자 화면에 접근하지 못할 수 있습니다.</Alert>

    <Panel title="등록된 네트워크" description="서버에 등록된 활성·비활성 범위를 모두 표시합니다.">
      <DataTable caption="관리자 IP 허용목록" columns={columns} rows={props.items ?? []} rowKey={(row) => row.id} state={props.listState} onRetry={retryList} />
    </Panel>

    {props.selected ? <Panel title={props.selected.cidr} description={`허용목록 ID ${props.selected.id}`} action={<Button size="small" variant="quiet" onClick={props.onClearSelection}>선택 닫기</Button>}>
      <DetailList items={[
        { label: "상태", value: statusBadge(props.selected.active ? "ACTIVE" : "DISABLED") },
        { label: "설명", value: props.selected.description || "미등록" },
        { label: "등록 관리자 ID", value: props.selected.createdByAdminLoginId ?? "—" },
        { label: "등록일시", value: date(props.selected.createdAt) },
        { label: "수정일시", value: date(props.selected.updatedAt) },
      ]} />
      {lastActiveSelected ? <Alert persistent variant="information" title="마지막 활성 범위입니다">관리자 접근이 모두 차단되지 않도록 이 범위는 비활성화할 수 없습니다. 다른 범위를 먼저 등록해 주세요.</Alert> : null}
      <div className="admin-detail-actions">
        <GuidedActionButton size="small" variant="danger" blockedReason={!props.canManage ? "IP 허용목록 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요." : !props.selected.active ? "이미 비활성화된 네트워크입니다." : lastActiveSelected ? "마지막 활성 네트워크는 비활성화할 수 없습니다. 다른 허용 범위를 먼저 등록해 주세요." : null} onClick={() => openEditor({ kind: "deactivate", item: props.selected! })}>네트워크 비활성화</GuidedActionButton>
      </div>
    </Panel> : null}

    <Dialog
      open={Boolean(editor)}
      title={editor?.kind === "deactivate" ? "허용 네트워크를 비활성화할까요?" : "허용 네트워크를 추가할까요?"}
      description={editor?.kind === "deactivate" ? `${editor.item.cidr} · 허용목록 ID ${editor.item.id}` : "숫자 IP 주소와 prefix를 CIDR 형식으로 입력합니다."}
      closeDisabled={pending}
      onClose={closeEditor}
      size="medium"
      tone={editor?.kind === "deactivate" ? "danger" : "default"}
      secondaryAction={<Button size="small" variant="quiet" disabled={pending} onClick={closeEditor}>취소</Button>}
      primaryAction={<GuidedActionButton size="small" type="submit" form={formId} variant={editor?.kind === "deactivate" ? "danger" : "primary"} isLoading={pending} blockedReason={!props.canManage ? "IP 허용목록 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요." : requestBlocked ? "직전 요청의 처리 결과가 확정되지 않았습니다. 목록 다시 조회를 눌러 최신 상태를 확인해 주세요." : null}>{editor?.kind === "deactivate" ? "비활성화" : "네트워크 추가"}</GuidedActionButton>}
    >
      {requestError ? <Alert persistent variant="danger" title={requestBlocked ? "최신 상태 확인 필요" : "요청을 처리하지 못했습니다"} action={requestBlocked ? <Button size="small" variant="quiet" onClick={() => void reconcile()}>목록 다시 조회</Button> : undefined}>{requestError}</Alert> : null}
      {editor ? <form className="admin-command" id={formId} noValidate onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        {editor.kind === "create" ? <>
          <Field ref={setRef("cidr")} compact label="CIDR" required maxLength={64} value={editor.values.cidr} error={errors.cidr} description="예: 192.0.2.0/24 또는 2001:db8::/32" onChange={(event) => updateCreate("cidr", event.currentTarget.value)} />
          <Field ref={setRef("description")} compact label="설명" maxLength={255} value={editor.values.description} error={errors.description} onChange={(event) => updateCreate("description", event.currentTarget.value)} />
        </> : <Alert persistent variant="warning" title="접근 가능 범위를 확인해 주세요">비활성화 후에는 이 CIDR에서 새 관리자 세션을 시작하지 못할 수 있습니다. 성공 여부는 서버 목록을 다시 조회해 확정합니다.</Alert>}
      </form> : null}
    </Dialog>
  </>;
}
